import React, { useState, useEffect } from 'react';
import { Box, LinearProgress, Typography, Alert, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import keepAliveService from '../../services/keepAliveService';

/**
 * Long-Running Operation Indicator
 * Shows progress and elapsed time for long-running operations
 */
const LongRunningOperationIndicator = () => {
  const [isActive, setIsActive] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [message, setMessage] = useState('Operation in progress...');

  useEffect(() => {
    const handleKeepAliveEvent = (event, data) => {
      switch (event) {
        case 'started':
          setIsActive(true);
          setElapsedTime(0);
          setMessage('Operation in progress...');
          break;
        case 'stopped':
          setIsActive(false);
          setElapsedTime(0);
          break;
        case 'heartbeat':
          if (data.elapsed) {
            setElapsedTime(data.elapsed);
          }
          break;
        case 'error':
          setMessage('Connection issue detected. Operation may still be running...');
          break;
        default:
          break;
      }
    };

    keepAliveService.addListener(handleKeepAliveEvent);
    
    // Check initial state
    setIsActive(keepAliveService.getActive());
    if (keepAliveService.getActive()) {
      setElapsedTime(keepAliveService.getElapsedTime());
    }

    // Update elapsed time every second when active
    const interval = setInterval(() => {
      if (keepAliveService.getActive()) {
        setElapsedTime(keepAliveService.getElapsedTime());
      }
    }, 1000);

    return () => {
      keepAliveService.removeListener(handleKeepAliveEvent);
      clearInterval(interval);
    };
  }, []);

  const formatElapsedTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  if (!isActive) {
    return null;
  }

  return (
    <Alert
      severity="info"
      icon={false}
      sx={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 9999,
        minWidth: 300,
        maxWidth: 400,
        boxShadow: 3
      }}
      action={
        <IconButton
          aria-label="close"
          color="inherit"
          size="small"
          onClick={() => {
            // Don't stop the operation, just hide the indicator
            setIsActive(false);
          }}
        >
          <CloseIcon fontSize="inherit" />
        </IconButton>
      }
    >
      <Box>
        <Typography variant="body2" fontWeight="bold" gutterBottom>
          {message}
        </Typography>
        <LinearProgress sx={{ mt: 1, mb: 1 }} />
        <Typography variant="caption" color="text.secondary">
          Elapsed: {formatElapsedTime(elapsedTime)}
        </Typography>
      </Box>
    </Alert>
  );
};

export default LongRunningOperationIndicator;

